import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Card, Divider, Text } from '@factorialco/deprecated-design-system'
import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import {
  CompensationsValueObjectsPaymentRequestStatusEnum,
  CompensationsValueObjectsResultTypeEnum,
  PaymentsSidepanelDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import formatCurrency from 'modules/core/lib/formatCurrency'
import i18n from 'modules/core/lib/i18n'

import { PaymentRequestStatus, type PaymentStatus } from 'modules/compensations/lib/constants'

import SegmentedProgressBar from './SegmentedProgressBar'
import TreasuryCallout from './TreasuryCallout'

type PaymentRequestItem = {
  status: CompensationsValueObjectsPaymentRequestStatusEnum
  amountCents: number
}

type PayrollRunTotal = {
  resultType: CompensationsValueObjectsResultTypeEnum
  totalAmountInCents: number | string
}

const PAYROLL_RESULT_TYPE: CompensationsValueObjectsResultTypeEnum = 'payroll_result'

type PaymentStatusSegment = {
  status: PaymentStatus
  amountCents: number
}

const createStatusSegment = (status: PaymentStatus, amountCents: number): PaymentStatusSegment => ({
  status,
  amountCents,
})

const calculatePayrollResultTotal = (totals: PayrollRunTotal[] | undefined | null): number => {
  if (!totals) return 0

  return totals
    .filter((total) => total.resultType === PAYROLL_RESULT_TYPE)
    .reduce((acc: number, total) => {
      const amount =
        typeof total.totalAmountInCents === 'number'
          ? total.totalAmountInCents
          : Number(total.totalAmountInCents) || 0
      return acc + amount
    }, 0)
}

const calculatePaymentRequestsTotal = (paymentRequestItems: PaymentRequestItem[]): number => {
  return paymentRequestItems.reduce((acc, paymentRequest) => acc + paymentRequest.amountCents, 0)
}

const calculateStatusSegments = (
  paymentRequestItems: PaymentRequestItem[]
): PaymentStatusSegment[] => {
  const statusMap = new Map<CompensationsValueObjectsPaymentRequestStatusEnum, number>()

  paymentRequestItems.forEach((paymentRequestItem) => {
    const current = statusMap.get(paymentRequestItem.status) || 0
    statusMap.set(paymentRequestItem.status, current + paymentRequestItem.amountCents)
  })

  return Array.from(statusMap.entries()).map(([status, amountCents]) =>
    createStatusSegment(status satisfies PaymentStatus, amountCents)
  )
}

const calculatePendingToPayCents = (
  payrollResultTotalCents: number,
  paymentRequestsTotal: number
): number => {
  return Math.max(0, payrollResultTotalCents - paymentRequestsTotal)
}

const buildProgressBarSegments = (
  statusSegments: PaymentStatusSegment[],
  pendingToPayCents: number
): Array<{ amount: number; status: PaymentStatus }> => {
  const allSegments: PaymentStatusSegment[] = [
    ...statusSegments,
    ...(pendingToPayCents > 0
      ? [createStatusSegment(PaymentRequestStatus.PENDING_TO_PAY, pendingToPayCents)]
      : []),
  ]

  return allSegments.map((segment) => ({
    amount: segment.amountCents,
    status: segment.status,
  }))
}

const PaymentsSidepanel = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { id } = useParams()

  const { data, loading } = useQuery(PaymentsSidepanelDocument, {
    variables: { payrollRunId: Number(id) },
    skip: !id,
  })

  const paymentRequestItems = useMemo(
    () => data?.compensations.paymentRequestItemsConnection.nodes ?? [],
    [data?.compensations.paymentRequestItemsConnection]
  )

  const payrollResultTotalCents = useMemo(
    () => calculatePayrollResultTotal(data?.compensations?.payrollRunTotalsConnection?.nodes),
    [data?.compensations?.payrollRunTotalsConnection]
  )

  const paymentRequestsTotal = useMemo(
    () => calculatePaymentRequestsTotal(paymentRequestItems),
    [paymentRequestItems]
  )

  const statusSegments = useMemo(
    () => calculateStatusSegments(paymentRequestItems),
    [paymentRequestItems]
  )

  const pendingToPayCents = useMemo(
    () => calculatePendingToPayCents(payrollResultTotalCents, paymentRequestsTotal),
    [payrollResultTotalCents, paymentRequestsTotal]
  )

  const onClose = () => {
    if (!id) return

    navigate(navigation.compensations.cycles.id.fullPath({ id }))
  }

  const totalAmountCents = Math.max(payrollResultTotalCents, paymentRequestsTotal)

  const progressBarSegments = useMemo(
    () => buildProgressBarSegments(statusSegments, pendingToPayCents),
    [statusSegments, pendingToPayCents]
  )

  const compensations = data?.compensations
  const hasPendingConnection =
    compensations &&
    typeof compensations === 'object' &&
    'paymentRequestPendingItemsConnection' in compensations

  const pendingPaymentItemsCount = hasPendingConnection
    ? (compensations?.paymentRequestPendingItemsConnection?.totalCount ?? 0)
    : 0

  const isPaymentGenerationInProgress = useMemo(
    () =>
      !loading &&
      paymentRequestItems.length === 0 &&
      pendingPaymentItemsCount === 0 &&
      payrollResultTotalCents > 0,
    [loading, paymentRequestItems.length, pendingPaymentItemsCount, payrollResultTotalCents]
  )

  return (
    <Sidepanel width="wide" open title={i18n.t('compensations.payments.title')} onClose={onClose}>
      {loading ? (
        <Loading />
      ) : isPaymentGenerationInProgress ? (
        <OneEmptyState
          title={i18n.t('compensations.payments.empty_state.title')}
          description={i18n.t('compensations.payments.empty_state.description')}
          emoji="⏳"
        />
      ) : (
        <Box flexDirection="column" gap="s24">
          <Card>
            <Card.Title>{i18n.t('compensations.payments.card.title')}</Card.Title>
            <Card.Content>
              <Box flexDirection="column" gap="s16" width="full">
                <Box flexDirection="column" gap="s4">
                  <Text color="black" size="500" weight="semibold">
                    {formatCurrency(totalAmountCents, undefined, null, undefined)}
                  </Text>
                  <Text.Normal size="200" color="textSecondary">
                    {i18n.t('compensations.payments.card.total')}
                  </Text.Normal>
                </Box>

                <Divider />

                <SegmentedProgressBar segments={progressBarSegments} total={totalAmountCents} />
              </Box>
            </Card.Content>
          </Card>

          {data?.finance?._policies?.sidebarSeeFinancePaymentsSubLevelItem?.can && (
            <TreasuryCallout />
          )}
        </Box>
      )}
    </Sidepanel>
  )
}

export default PaymentsSidepanel
